import type { PianoChart } from '@shared/pianoChart';
import { bigBannerScale, countdownNumberAnim, goBannerAnim } from './pianoAnimCurves';
import { GO_BANNER_LIFE, LANE_GAP_PX } from './pianoRenderConstants';
import { withAlpha } from './pianoRenderColour';
import type { PianoLayout } from '../pianoPlayfieldGeometry';
import type {
  PianoFloatingText,
  PianoRenderInput,
  PianoScorePop,
} from './pianoRenderTypes';
import type { PianoFxSystem } from './pianoFx';

/**
 * Top-of-screen overlays that aren't part of the lane/tile/HUD
 * vocabulary: floating verdict pops, +N score flyers, the lead-in
 * countdown number, the "GO!" banner the renderer pops itself, and
 * the paused dim overlay. Big floating-text banners trigger a one-
 * shot entry shockwave via the FX system the first frame they
 * appear; the renderer owns the WeakSet that gates that "first
 * sight" to avoid the orchestrator double-firing across frames.
 *
 * `LeadInBannerState` is exposed so the orchestrator can keep one
 * instance across frames — it tracks the countdown→null transition
 * and ages the "GO!" banner toward expiry.
 */

export class LeadInBannerState {
  /**
   * Last frame's countdown value. Used to detect the lead-in→running
   * boundary so the renderer can pop its own "GO!" banner without
   * the session having to push a floating text for it.
   */
  private lastCountdown: number | null = null;
  private goPulseRemaining = 0;
  /**
   * First-sight tracker for big banners — fires a one-shot entry
   * shockwave the frame a new banner appears. Per-instance (not
   * module-level) so multiple concurrent renderers (e.g. the
   * `RenderDebugPage` mounting more than one mode at once) don't
   * silently steal each other's entry shockwave.
   */
  readonly consumedBigBanners = new WeakSet<PianoFloatingText>();

  reset(): void {
    this.lastCountdown = null;
    this.goPulseRemaining = 0;
  }

  /**
   * Detect the lead-in→running boundary (countdown was positive, now
   * null/0) and trigger a renderer-owned "GO!" banner so the session
   * doesn't need a separate floating-text push for it. Also ages the
   * GO banner toward expiry.
   */
  advance(countdown: number | null, dt: number): void {
    const prev = this.lastCountdown;
    this.lastCountdown = countdown;
    if (prev !== null && prev > 0 && (countdown === null || countdown <= 0)) {
      this.goPulseRemaining = GO_BANNER_LIFE;
    }
    if (dt > 0 && this.goPulseRemaining > 0) {
      this.goPulseRemaining = Math.max(0, this.goPulseRemaining - dt);
    }
  }

  /** True when the renderer should call drawCountdown this frame. */
  shouldDrawCountdown(countdown: number | null): boolean {
    return (countdown !== null && countdown > 0) || this.goPulseRemaining > 0;
  }

  getGoPulseRemaining(): number {
    return this.goPulseRemaining;
  }
}

export function drawFloatingTexts(
  ctx: CanvasRenderingContext2D,
  input: PianoRenderInput,
  layout: PianoLayout,
  canvasWidth: number,
  canvasHeight: number,
  fx: PianoFxSystem,
  bannerState: LeadInBannerState,
  reducedMotion: boolean,
): void {
  if (input.floatingTexts.length === 0) {
    return;
  }
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const bannerY = canvasHeight * 0.16;
  for (const t of input.floatingTexts) {
    const alpha = Math.max(0, Math.min(1, t.life / t.maxLife));
    if (t.big) {
      // Top-of-canvas banner — sized + positioned to stay clear of the
      // tile approach path (tiles travel from y=0 down to
      // y=hitLineY ~0.85*h, so any banner inside that range briefly
      // occludes incoming notes). Sat at y~0.16*h, just below the HP
      // bar; tiles only cross this zone in their first ~150ms after
      // spawning when the player can't react yet.
      const elapsed = 1 - alpha;
      const scale = reducedMotion ? 1 : bigBannerScale(elapsed);
      const fontPx = Math.round(36 * scale);
      // First-frame entry: punch an outer ring shockwave around the
      // banner so it reads as "burst onto screen" rather than "faded
      // in" — pairs with the new pop-and-settle scale curve.
      if (!bannerState.consumedBigBanners.has(t)) {
        bannerState.consumedBigBanners.add(t);
        fx.spawnShockwaveDirect({
          x: canvasWidth / 2,
          y: bannerY,
          startR: 24,
          endR: 220,
          life: 0.45,
          thickness: 3,
          colour: t.colour,
          alpha: 0.75,
        });
      }
      ctx.save();
      ctx.font = `bold ${fontPx}px system-ui, sans-serif`;
      ctx.fillStyle = withAlpha(t.colour, alpha);
      if (!reducedMotion) {
        ctx.shadowColor = t.colour;
        ctx.shadowBlur = 18 * alpha;
      }
      ctx.fillText(t.text, canvasWidth / 2, bannerY);
      ctx.restore();
    } else {
      ctx.font = 'bold 20px system-ui, sans-serif';
      ctx.fillStyle = withAlpha(t.colour, alpha);
      const x = layout.lane0X + t.lane * (layout.laneWidth + LANE_GAP_PX) + layout.laneWidth / 2;
      ctx.fillText(t.text, x, t.y);
    }
  }
}

export function drawScorePops(
  ctx: CanvasRenderingContext2D,
  pops: ReadonlyArray<PianoScorePop>,
  reducedMotion: boolean,
): void {
  if (pops.length === 0) {
    return;
  }
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const pop of pops) {
    const t = 1 - pop.life / pop.maxLife;
    const easeOut = 1 - Math.pow(1 - t, 3);
    const x = pop.startX + (pop.targetX - pop.startX) * easeOut;
    // Arc the y motion: rise then fall toward target.
    const arcLift = Math.sin(t * Math.PI) * 40;
    const y = pop.startY + (pop.targetY - pop.startY) * easeOut - arcLift;
    const alpha = Math.max(0, Math.min(1, pop.life / pop.maxLife));
    const size = pop.big ? 22 : 16;
    ctx.font = `bold ${size}px system-ui, sans-serif`;
    ctx.fillStyle = withAlpha(pop.colour, alpha);
    if (pop.big && !reducedMotion) {
      ctx.save();
      ctx.shadowColor = pop.colour;
      ctx.shadowBlur = 10 * alpha;
      ctx.fillText(pop.text, x, y);
      ctx.restore();
    } else {
      ctx.fillText(pop.text, x, y);
    }
  }
}

export function drawCountdown(
  ctx: CanvasRenderingContext2D,
  countdown: number | null,
  width: number,
  height: number,
  palette: PianoChart['palette'],
  goPulseRemaining: number,
  reducedMotion: boolean,
): void {
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const centreX = width / 2;
  const centreY = height / 2;

  // Current integer (3 → 2 → 1) with a per-number pop / settle / fade
  // arc — `tWithin` runs 0..1 across each integer's display window so
  // the number snaps in fast, holds, then shrinks just before the next
  // one (or "GO!") takes over.
  if (countdown !== null && countdown > 0) {
    const number = Math.max(1, Math.ceil(countdown));
    const tWithin = Math.max(0, Math.min(1, number - countdown));
    const { scale, alpha } = countdownNumberAnim(tWithin, reducedMotion);
    if (alpha > 0.01) {
      const fontPx = Math.round(80 * scale);
      ctx.font = `bold ${fontPx}px system-ui, sans-serif`;
      ctx.fillStyle = withAlpha(palette.ui, alpha);
      if (!reducedMotion) {
        ctx.save();
        ctx.shadowColor = palette.ui;
        ctx.shadowBlur = 18 * alpha;
        ctx.fillText(String(number), centreX, centreY);
        ctx.restore();
      } else {
        ctx.fillText(String(number), centreX, centreY);
      }
    }
  }

  // "GO!" climax frame — the renderer fires this itself the moment
  // the countdown transitions to null/0 (see LeadInBannerState.advance).
  // Bigger overshoot + brighter glow than the integers so the start-
  // of-loop reads as a real release of tension.
  if (goPulseRemaining > 0) {
    const t = 1 - goPulseRemaining / GO_BANNER_LIFE;
    const { scale, alpha } = goBannerAnim(t, reducedMotion);
    if (alpha > 0.01) {
      const fontPx = Math.round(88 * scale);
      ctx.font = `bold ${fontPx}px system-ui, sans-serif`;
      const goColour = palette.padPerfect;
      ctx.fillStyle = withAlpha(goColour, alpha);
      if (!reducedMotion) {
        ctx.save();
        ctx.shadowColor = goColour;
        ctx.shadowBlur = 32 * alpha;
        ctx.fillText('GO!', centreX, centreY);
        ctx.restore();
      } else {
        ctx.fillText('GO!', centreX, centreY);
      }
    }
  }
}

export function drawPausedOverlay(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  palette: PianoChart['palette'],
): void {
  ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
  ctx.fillRect(0, 0, width, height);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = palette.ui;
  ctx.font = 'bold 36px system-ui, sans-serif';
  ctx.fillText('Paused', width / 2, height / 2);
}
